import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer, { GOOGLE_BUSINESS_URL } from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Star, Quote, Shield, CheckCircle2, ExternalLink, Filter } from "lucide-react";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return ""; }
}

const initialsOf = (name) => (name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

export default function Avis() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("all"); // all | top | recent
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/public/reviews", { params: { limit: 50, min_rating: 1 } }).then((r) => r.data),
      api.get("/public/stats").then((r) => r.data),
    ]).then(([r, s]) => {
      setReviews(r);
      setStats(s);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (tab === "top") return [...reviews].filter((r) => r.rating >= 5);
    if (tab === "recent") return [...reviews].slice(0, 12);
    return reviews;
  }, [reviews, tab]);

  // JSON-LD for SEO: AggregateRating + individual reviews
  const jsonLd = useMemo(() => {
    if (!stats || !reviews.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "DepannRoute",
      "url": "https://depannroute.com",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": stats.rating_avg || 0,
        "reviewCount": stats.rating_count || 0,
        "bestRating": 5,
        "worstRating": 1,
      },
      "review": reviews.slice(0, 20).map((r) => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.client_name || "Client DepannRoute" },
        "datePublished": r.created_at,
        "reviewBody": r.comment || "",
        "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5, "worstRating": 1 },
        "itemReviewed": {
          "@type": "Service",
          "name": r.pro_company || r.pro_name || "Dépanneur",
          "provider": { "@type": "Organization", "name": "DepannRoute" },
        },
      })),
    };
  }, [stats, reviews]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="avis-page">
      <SEO
        title="Avis clients &amp; dépanneurs certifiés — DepannRoute"
        description="Découvrez les avis vérifiés des clients sur les dépanneurs professionnels DepannRoute en France, Belgique, Espagne et Allemagne. Notes, commentaires et avis Google Business."
        canonical="https://depannroute.com/avis"
        jsonLd={jsonLd}
      />
      <Header />

      <section className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-widest font-bold text-orange-600">Avis certifiés</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-slate-900 mt-2">
            La confiance de nos clients &amp; pros.
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-3xl">
            Chaque mission se termine par une <strong>notation vérifiée</strong>. Nos avis sont
            authentiques, post-mission, non modifiables par le dépanneur.
          </p>

          {/* Aggregate stats strip */}
          {stats && (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="avis-stats">
              <Stat label="Note moyenne" value={stats.rating_avg > 0 ? `${stats.rating_avg}/5` : "—"} icon={Star} accent />
              <Stat label="Avis vérifiés" value={stats.rating_count} icon={CheckCircle2} />
              <Stat label="Dépanneurs pros" value={stats.pro_count} icon={Shield} />
              <Stat label="Missions réalisées" value={stats.mission_count} icon={CheckCircle2} />
            </div>
          )}
        </div>
      </section>

      {/* Google Business card */}
      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 text-white rounded-md p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center gap-6 relative overflow-hidden">
            <div className="absolute inset-0 safety-stripes opacity-[0.05]" />
            <div className="relative flex items-center gap-4">
              <div className="h-14 w-14 rounded-md bg-white flex items-center justify-center shrink-0">
                <svg viewBox="0 0 48 48" className="h-9 w-9" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Avis Google certifiés</div>
                <h2 className="font-display font-bold text-2xl sm:text-3xl mt-1">Retrouvez-nous aussi sur Google</h2>
                <p className="text-slate-300 text-sm mt-1 max-w-xl">Tous nos avis sont également référencés sur notre fiche Google Business pour une transparence totale.</p>
              </div>
            </div>
            <div className="relative ml-auto">
              <a href={GOOGLE_BUSINESS_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-google-reviews">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-md px-5 py-5">
                  Voir les avis Google
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="avis-tabs">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-bold mr-2"><Filter className="inline h-3 w-3 mr-1" />Filtrer :</span>
            {[
              { v: "all", label: "Tous les avis" },
              { v: "top", label: "5 étoiles" },
              { v: "recent", label: "Plus récents" },
            ].map((t) => (
              <button
                key={t.v}
                onClick={() => setTab(t.v)}
                data-testid={`tab-${t.v}`}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${tab === t.v ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-slate-500 text-sm py-16 text-center">Chargement des avis…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center">
              <Star className="h-10 w-10 mx-auto text-slate-300" />
              <div className="font-display font-bold text-xl mt-3 text-slate-900">Pas encore d'avis publiés</div>
              <p className="text-sm text-slate-600 mt-1">Les avis apparaîtront ici après les premières missions.</p>
              <Link to="/register?role=client"><Button className="mt-4 bg-orange-600 hover:bg-orange-700">Publier ma première demande</Button></Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="avis-grid">
              {filtered.map((r) => <ReviewCard key={r.review_id} r={r} />)}
            </div>
          )}
        </div>
      </section>

      <Footer stats={stats} />
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent = false }) {
  return (
    <div className={`rounded-md border p-5 ${accent ? "bg-slate-900 text-white border-slate-800" : "bg-white border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "text-orange-400" : "text-slate-500"}`}>{label}</div>
        <Icon className={`h-4 w-4 ${accent ? "text-orange-400" : "text-slate-400"}`} />
      </div>
      <div className={`font-display font-extrabold text-3xl mt-2 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function ReviewCard({ r }) {
  return (
    <article className="bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-0.5 hover:shadow-md transition-all flex flex-col" data-testid={`review-${r.review_id}`} itemScope itemType="https://schema.org/Review">
      <meta itemProp="datePublished" content={r.created_at} />
      <div className="flex items-center gap-1 text-amber-500 mb-2" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
        <meta itemProp="ratingValue" content={r.rating} />
        <meta itemProp="bestRating" content="5" />
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-amber-500" : "text-slate-300"}`} />
        ))}
        <span className="ml-auto text-[11px] text-slate-400">{formatDate(r.created_at)}</span>
      </div>
      <Quote className="h-5 w-5 text-orange-200" />
      <p className="mt-2 text-slate-700 leading-relaxed text-sm line-clamp-6 flex-1" itemProp="reviewBody">
        {r.comment || "—"}
      </p>
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold">
          {initialsOf(r.client_name || "Client")}
        </div>
        <div className="min-w-0" itemProp="author" itemScope itemType="https://schema.org/Person">
          <div className="text-sm font-semibold text-slate-900" itemProp="name">{r.client_name || "Client vérifié"}</div>
          <div className="text-xs text-slate-500 truncate">
            Intervention {r.pro_company ? <>par <strong className="text-slate-700">{r.pro_company}</strong></> : r.pro_name ? <>par <strong className="text-slate-700">{r.pro_name}</strong></> : "par un pro certifié"}
            {r.pro_city ? <> · {r.pro_city}</> : null}
          </div>
        </div>
      </div>
    </article>
  );
}
